// CDN utilities for optimized image loading
import { getOptimizedCDNUrl } from './cdn.utils';

/**
 * Get the full CDN URL for an image with optimization params
 * Uses ?auto=1&hint=wifi by default for network-aware optimization
 * 
 * @param {string} imageName - The image filename
 * @param {Object} [options] - CDN options (auto, hint, width, quality, format, raw)
 * @returns {string|null} Full CDN URL or null
 */
export const getCMSImageURL = (imageName, options = {}) => {
  if (!imageName) return null;
  return getOptimizedCDNUrl(imageName, options);
};

/**
 * Get optimized image data with LQIP and WebP support
 * Returns an object with src, webpSrc, and lqipSrc
 * 
 * CMS provides: src, webp, lqip (not webpSrc, lqipSrc)
 * Logic:
 * - If lqip exists, show it first as placeholder
 * - If webp exists, prefer it over src
 * - Fall back to src if no webp
 * 
 * @param {object} photoItem - Photo object from contentPhotos array
 * @param {Object} [options] - CDN options (quality, width, raw, etc.)
 * @returns {object} { src, webpSrc, lqipSrc }
 */
export const getOptimizedImageData = (photoItem, options = {}) => {
  if (!photoItem) return { src: null, webpSrc: null, lqipSrc: null };

  // CMS field names are: src, webp, lqip (not webpSrc, lqipSrc)
  const srcPath = photoItem.src;
  const webpPath = photoItem.webp || photoItem.webpSrc; // Support both for backward compat
  const lqipPath = photoItem.lqip || photoItem.lqipSrc; // Support both for backward compat

  return {
    // Primary source (for fallback if webp fails)
    src: srcPath ? getCMSImageURL(srcPath, options) : null,
    // WebP source (preferred format)
    webpSrc: webpPath ? getCMSImageURL(webpPath, options) : null,
    // LQIP source (placeholder before main image loads - no quality override needed)
    lqipSrc: lqipPath ? getCMSImageURL(lqipPath, options.raw ? { raw: true } : {}) : null,
  };
};

/**
 * Find and get optimized image data for a specific type from contentPhotos
 * @param {string} type - Image type (e.g., 'verticalCover', 'sliderCover', 'poster')
 * @param {Array} contentPhotos - Array of photo objects
 * @param {string} preferredDevice - Preferred device type ('TV', 'mobile', etc.)
 * @param {Object} [options] - CDN options (quality, width, raw, etc.)
 * @returns {object} { src, webpSrc, lqipSrc }
 */
export const getOptimizedImageForType = (type = "", contentPhotos = [], preferredDevice = "TV", options = {}) => {
  if (!contentPhotos || !Array.isArray(contentPhotos)) {
    return { src: null, webpSrc: null, lqipSrc: null };
  }

  // Try to find image with preferred device first
  let photoItem = contentPhotos.find((x) => x.type === type && x.device === preferredDevice);

  // Fallback to any device for this type
  if (!photoItem) {
    photoItem = contentPhotos.find((x) => x.type === type);
  }

  // Fallback to poster type
  if (!photoItem) {
    photoItem = contentPhotos.find((x) => x.type === "poster");
  }

  return getOptimizedImageData(photoItem, options);
};

/**
 * Legacy function - get image URL for a specific type
 * @deprecated Use getOptimizedImageForType instead for LQIP support
 */
export const getImageForType = (type = "", contentPhotos = []) =>
  getCMSImageURL(
    contentPhotos?.find((x) => x.type === type && x.device === "TV")?.src ||
    contentPhotos?.find((x) => x.type === type)?.src ||
    contentPhotos?.find((x) => x.type === "poster")?.src
  );

/**
 * Get all image variants (src, webp, lqip) for vertical cover images
 * @param {Array} contentPhotos - Array of photo objects
 * @returns {object} { src, webpSrc, lqipSrc }
 */
export const getVerticalCoverImage = (contentPhotos = []) =>
  getOptimizedImageForType("verticalCover", contentPhotos, "TV", {
    quality: 90,
    width: 300,
    format: "jpeg",
  });

/**
 * Get all image variants for horizontal/slider cover images
 * @param {Array} contentPhotos - Array of photo objects
 * @returns {object} { src, webpSrc, lqipSrc }
 */
export const getSliderCoverImage = (contentPhotos = []) =>
  getOptimizedImageForType("sliderCover", contentPhotos, "TV", { raw: true });

/**
 * Get all image variants for poster images
 * @param {Array} contentPhotos - Array of photo objects
 * @returns {object} { src, webpSrc, lqipSrc }
 */
export const getPosterImage = (contentPhotos = []) =>
  getOptimizedImageForType("poster", contentPhotos, "TV", {
    quality: 90,
    width: 300,
    format: "jpeg",
  });

/**
 * Get all image variants for video logo images
 * @param {Array} contentPhotos - Array of photo objects
 * @returns {object} { src, webpSrc, lqipSrc }
 */
export const getVideoLogoImage = (contentPhotos = []) =>
  getOptimizedImageForType("videoLogo", contentPhotos, "TV", { raw: true });

/**
 * Tags guide for content categorization
 */
export const tagsGuide = {
  "ترکی دیلینده": "TURKISH_NATIVE",
  "گزارش ترکی": "TURKISH_REPORT",
  "پخش زنده": "LIVE_STREAM",
  بزودی: "COMING_SOON",
  "آنلاین باخیش": "ONLINE_CINEMA",
  آرشیو: "ARCHIVE",
};
